import type {
    InvoicingSettings,
    InvoicingSettingsUpdatePayload,
} from '../../../../../hooks/useGetSettings';
import type { LkqPricingEntry, LkqPricingEntryFactory } from '../../../../../utils/lkqPricingRows';

/** The settings sections whose rows are pricing rules. */
export type PricingSectionId =
    | 'invoicing-markups'
    | 'invoicing-discounts'
    | 'invoicing-flags';

/**
 * A stored pricing entry, as any of the four tables holds it. Both percentage
 * fields are optional here and exactly one is set per table; `percentageOf`
 * and `pricingEntryFactory` are the only places that pick which.
 */
export interface PricingEntry extends LkqPricingEntry {
    /** Always set on a real row; the tables are keyed by supplier id. */
    supplierId: string;
    markupPercentage?: number;
    discountPercentage?: number;
    expectedDiscountPercentage?: number;
}

export interface PricingTableSpec {
    /** Tab id, which also travels in a row's URL. */
    tabId: 'parts' | 'sublet' | 'flags';
    tabLabel: string;
    /** Field of the invoicing subdocument holding these entries. */
    field:
        | 'lkqMarkups'
        | 'subletMarkups'
        | 'lkqDiscounts'
        | 'subletDiscounts'
        | 'discountFlagExpectations';
    percentageKey: 'markupPercentage' | 'discountPercentage' | 'expectedDiscountPercentage';
    /** How one row reads in copy: "markup", "sublet discount". */
    rowNoun: string;
    /** Percentage column header. */
    percentColumnLabel: string;
    /** Label and description of the percentage field in the row's editor. */
    percentRowLabel: string;
    percentRowDescription: string;
    emptyText: string;
    /** Heading of the row's editor. */
    detailTitle: string;
    /** How the rule's outcome reads: "then apply a markup of" + "25%". */
    outcomeLead: string;
}

const MARKUP_TABLES: PricingTableSpec[] = [
    {
        tabId: 'parts',
        tabLabel: 'Parts',
        field: 'lkqMarkups',
        percentageKey: 'markupPercentage',
        rowNoun: 'markup',
        percentColumnLabel: '% markup',
        percentRowLabel: 'Markup',
        percentRowDescription:
            'The markup agreed with this vendor, used to calculate the list price of parts-specific lines.',
        emptyText: 'No parts-specific markups configured yet.',
        detailTitle: 'Parts-specific markup',
        outcomeLead: 'then apply a markup of',
    },
    {
        tabId: 'sublet',
        tabLabel: 'Sublet',
        field: 'subletMarkups',
        percentageKey: 'markupPercentage',
        rowNoun: 'sublet markup',
        percentColumnLabel: '% markup',
        percentRowLabel: 'Markup',
        percentRowDescription:
            'The markup agreed with this vendor, used to calculate the list price of sublet lines.',
        emptyText: 'No sublet markups configured yet.',
        detailTitle: 'Sublet markup',
        outcomeLead: 'then apply a markup of',
    },
];

const DISCOUNT_TABLES: PricingTableSpec[] = [
    {
        tabId: 'parts',
        tabLabel: 'Parts',
        field: 'lkqDiscounts',
        percentageKey: 'discountPercentage',
        rowNoun: 'discount',
        percentColumnLabel: '% discount',
        percentRowLabel: 'Discount',
        percentRowDescription:
            'The discount agreed with this vendor, used to calculate the cost of parts-specific lines from the list price. A matching discount overrides any markup that would otherwise apply.',
        emptyText: 'No parts-specific discounts configured yet.',
        detailTitle: 'Parts-specific discount',
        outcomeLead: 'then apply a discount of',
    },
    {
        tabId: 'sublet',
        tabLabel: 'Sublet',
        field: 'subletDiscounts',
        percentageKey: 'discountPercentage',
        rowNoun: 'sublet discount',
        percentColumnLabel: '% discount',
        percentRowLabel: 'Discount',
        percentRowDescription:
            'The discount agreed with this vendor, used to calculate the cost of sublet lines from the list price. A matching discount overrides any sublet markup that would otherwise apply.',
        emptyText: 'No sublet discounts configured yet.',
        detailTitle: 'Sublet discount',
        outcomeLead: 'then apply a discount of',
    },
];

/**
 * The discount flag expectations are the same rule with a different meaning:
 * rather than reconstructing a price, the percentage is what the shop expects
 * to be given, and an invoice implying less is flagged. One tab, because the
 * Flags section shows it beside its other cards rather than as a table of tabs.
 */
const DISCOUNT_FLAG_TABLES: PricingTableSpec[] = [
    {
        tabId: 'flags',
        tabLabel: 'Discount flag',
        field: 'discountFlagExpectations',
        percentageKey: 'expectedDiscountPercentage',
        rowNoun: 'discount flag',
        percentColumnLabel: 'Expected % discount',
        percentRowLabel: 'Expected discount',
        percentRowDescription:
            'The discount you expect to receive from this vendor. Invoices implying less are flagged for review.',
        emptyText: 'No discount flag expectations configured yet.',
        detailTitle: 'Discount flag',
        outcomeLead: 'then we expect a discount of',
    },
];

/** Tabs of each pricing section, in the order they appear. */
export const PRICING_TABLES: Record<PricingSectionId, PricingTableSpec[]> = {
    'invoicing-markups': MARKUP_TABLES,
    'invoicing-discounts': DISCOUNT_TABLES,
    'invoicing-flags': DISCOUNT_FLAG_TABLES,
};

export const pricingTableSpec = (
    sectionId: PricingSectionId,
    tabId: string,
): PricingTableSpec | undefined =>
    PRICING_TABLES[sectionId].find((spec) => spec.tabId === tabId);

/** One table's stored entries, from the invoicing settings or the draft. */
export const pricingEntriesOf = (
    spec: PricingTableSpec,
    invoicing: InvoicingSettings | InvoicingSettingsUpdatePayload,
): PricingEntry[] => (invoicing[spec.field] ?? []) as PricingEntry[];

/** Reads the percentage this table stores its rows under. */
export const percentageOf =
    (spec: PricingTableSpec) =>
    (entry: PricingEntry): number =>
        entry[spec.percentageKey] ?? 0;

/** Writes a stored entry for this table, one per company and state. */
export const pricingEntryFactory =
    (spec: PricingTableSpec): LkqPricingEntryFactory<PricingEntry> =>
    ({ company, state, values }) => ({
        insuranceCompanyId: company.id,
        insuranceCompanyName: company.name,
        insuranceGroupId: company.insuranceGroupId,
        parentInsuranceCompanyId: company.parentInsuranceCompanyId,
        parentInsuranceCompanyName: company.parentInsuranceCompanyName,
        supplierId: values.supplierId,
        supplierName: values.supplierName,
        ...(values.partKind ? { partKind: values.partKind } : {}),
        ...(values.make ? { make: values.make } : {}),
        state,
        [spec.percentageKey]: values.percentage,
    });

/**
 * The invoicing subdocument with one table's rows replaced, for a screen that
 * saves the whole subdocument itself (POST /settings/update) rather than
 * staging on the settings draft.
 *
 * The rows always come from this table's own reader or factory, so the
 * percentage it names is set on every one of them — which the optional fields
 * on `PricingEntry` cannot express, hence the assertion.
 */
export const pricingSettingsWith = (
    spec: PricingTableSpec,
    settings: InvoicingSettings,
    entries: PricingEntry[],
): InvoicingSettings =>
    ({ ...settings, [spec.field]: entries } as InvoicingSettings);

/**
 * One table's write, as a patch on the invoicing draft. Same assertion as
 * `pricingSettingsWith`: the payload's row type requires the percentage this
 * table names, and only this table's field is being written.
 */
export const pricingUpdate = (
    spec: PricingTableSpec,
    entries: PricingEntry[],
): Partial<InvoicingSettingsUpdatePayload> =>
    ({ [spec.field]: entries } as Partial<InvoicingSettingsUpdatePayload>);

/** Separator between a row's tab and its rule key in the URL. */
const ROW_ID_SEPARATOR = '~';

/**
 * A row's editor lives under its section, and a rule key alone would not say
 * which tab it came from, so the segment carries both. Rule keys contain
 * separators of their own, so the whole segment travels encoded.
 */
export const pricingRowPath = (
    sectionId: PricingSectionId,
    tabId: string,
    ruleKey: string,
): string =>
    `/settings/${sectionId}/${encodeURIComponent(`${tabId}${ROW_ID_SEPARATOR}${ruleKey}`)}`;

/** The tab and rule key a row's URL segment carries, if it is well formed. */
export const parsePricingRowId = (
    rowId: string,
): { tabId: string; ruleKey: string } | undefined => {
    const at = rowId.indexOf(ROW_ID_SEPARATOR);
    if (at <= 0) return undefined;
    const ruleKey = rowId.slice(at + 1);
    if (!ruleKey) return undefined;
    return { tabId: rowId.slice(0, at), ruleKey };
};
